import { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, EyeSlash, Warning, CheckCircle } from "@phosphor-icons/react";

/**
 * Payment-gateway credential card.
 *
 * Lets admins configure Authorize.net API Login ID + Transaction Key +
 * Public Client Key + environment from the UI, without needing to
 * redeploy with new env vars. Values are stored in MongoDB
 * (system_config.payment_gateway) and take precedence over env vars.
 *
 * The Transaction Key is masked once saved — enter a fresh value to
 * replace it, or type the literal string ``--CLEAR--`` to wipe it.
 * The API Login ID and Public Client Key are non-secret (they ship in
 * Accept.js in the browser) so they're shown in full.
 */
export const PaymentGatewayCard = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    api_login_id: "",
    transaction_key: "",
    public_client_key: "",
    environment: "production",
  });
  const [showKey, setShowKey] = useState(false);
  const [replaceKey, setReplaceKey] = useState(false);

  const fetchConfig = async () => {
    try {
      const r = await axios.get(`${API}/payment-gateway`);
      setConfig(r.data);
      setForm({
        api_login_id: r.data.api_login_id || "",
        transaction_key: "",
        public_client_key: r.data.public_client_key || "",
        environment: r.data.environment || "production",
      });
      setReplaceKey(false);
    } catch {
      toast.error("Failed to load payment gateway config");
    }
  };
  useEffect(() => { fetchConfig(); }, []);

  const handleSave = async () => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      const payload = {
        api_login_id: form.api_login_id.trim(),
        public_client_key: form.public_client_key.trim(),
        environment: form.environment,
      };
      // Only submit the transaction key if the user has explicitly opted
      // to replace it — otherwise the backend preserves the existing key.
      if (replaceKey) {
        payload.transaction_key = form.transaction_key.trim();
      }
      const r = await axios.put(`${API}/payment-gateway`, payload);
      setConfig({
        ...config,
        ...r.data,
        source: "database",
      });
      setReplaceKey(false);
      setForm((f) => ({ ...f, transaction_key: "" }));
      toast.success("Payment gateway credentials saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save credentials");
    } finally {
      setSaving(false);
    }
  };

  if (!config) return <Card><CardContent className="p-6 text-sm text-muted-foreground">Loading…</CardContent></Card>;

  const isSandbox = !config.api_login_id || !config.has_transaction_key || config.environment === "sandbox";

  return (
    <Card data-testid="payment-gateway-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Authorize.net Credentials
          {isSandbox ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200">
              <Warning size={12} weight="fill" /> Sandbox Mode
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200">
              <CheckCircle size={12} weight="fill" /> Live
            </span>
          )}
        </CardTitle>
        <CardDescription>
          These credentials are stored in the database and take precedence over environment variables. Changes take effect immediately — no redeploy needed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isSandbox && (
          <div className="rounded-md border-2 border-red-500 bg-red-50 dark:bg-red-950/40 dark:border-red-600 px-4 py-3 text-sm">
            <div className="font-bold text-red-900 dark:text-red-200 uppercase tracking-wide text-xs mb-1">
              Card transactions are NOT reaching Authorize.net.
            </div>
            <div className="text-red-900/90 dark:text-red-200/90">
              With no live credentials configured, every card charge is recorded as a fake &quot;SANDBOX-*&quot; transaction. Customers are not billed. Enter your live keys below to fix.
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="pg-login">API Login ID</Label>
          <Input
            id="pg-login"
            value={form.api_login_id}
            onChange={(e) => setForm((f) => ({ ...f, api_login_id: e.target.value }))}
            placeholder="e.g. 9b2K6bZ44"
            data-testid="pg-api-login-id"
            disabled={!isAdmin}
          />
          <p className="text-xs text-muted-foreground">
            Find this in the Auth.net dashboard → Account → Security → API Credentials & Keys.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="pg-txn">Transaction Key</Label>
          {!replaceKey && config.has_transaction_key ? (
            <div className="flex items-center gap-2">
              <Input
                id="pg-txn"
                value={config.transaction_key_masked || "•••••••••••••"}
                disabled
                className="font-mono"
                data-testid="pg-transaction-key-masked"
              />
              {isAdmin && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setReplaceKey(true)}
                  data-testid="pg-replace-txn-btn"
                >
                  Replace
                </Button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Input
                  id="pg-txn"
                  type={showKey ? "text" : "password"}
                  value={form.transaction_key}
                  onChange={(e) => setForm((f) => ({ ...f, transaction_key: e.target.value }))}
                  placeholder="Paste your Transaction Key"
                  className="font-mono pr-10"
                  data-testid="pg-transaction-key-input"
                  disabled={!isAdmin}
                />
                <button
                  type="button"
                  onClick={() => setShowKey((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="pg-toggle-visibility"
                  aria-label={showKey ? "Hide key" : "Show key"}
                >
                  {showKey ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {replaceKey && config.has_transaction_key && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => { setReplaceKey(false); setForm((f) => ({ ...f, transaction_key: "" })); }}
                  data-testid="pg-cancel-replace-btn"
                >
                  Cancel
                </Button>
              )}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Sensitive — server-side only. Never sent to the browser. Type <code>--CLEAR--</code> to wipe.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="pg-public">Public Client Key</Label>
          <Input
            id="pg-public"
            value={form.public_client_key}
            onChange={(e) => setForm((f) => ({ ...f, public_client_key: e.target.value }))}
            placeholder="Used by Accept.js in the browser"
            className="font-mono"
            data-testid="pg-public-client-key"
            disabled={!isAdmin}
          />
          <p className="text-xs text-muted-foreground">
            Non-secret. Used by the browser&apos;s Accept.js library to tokenize card data.
          </p>
        </div>

        <div className="space-y-2">
          <Label>Environment</Label>
          <Select
            value={form.environment}
            onValueChange={(v) => setForm((f) => ({ ...f, environment: v }))}
            disabled={!isAdmin}
          >
            <SelectTrigger data-testid="pg-environment"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="production" data-testid="pg-env-production">Production — live charges</SelectItem>
              <SelectItem value="sandbox" data-testid="pg-env-sandbox">Sandbox — Auth.net test environment</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isAdmin && (
          <div className="flex items-center justify-between pt-2">
            <div className="text-xs text-muted-foreground">
              Source: <span className="font-mono">{config.source}</span>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving || (!replaceKey && !form.api_login_id && !form.public_client_key)}
              data-testid="pg-save-btn"
            >
              {saving ? "Saving…" : "Save Credentials"}
            </Button>
          </div>
        )}
        {!isAdmin && (
          <div className="text-xs text-amber-600 dark:text-amber-400">
            Read-only — only administrators can edit payment gateway credentials.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentGatewayCard;
